import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useRouteLoaderData } from "react-router";
import { FieldsData, ResultSet } from "../../../../../../API/fields";
import { ResultSetGeoJSON } from "../../../../../../Component/Map/Layers/ResultSetLayer";
import { translateProductName } from "../../../../../../Component/Product/ProductName";
import { downloadFile } from "../../../../../../downloadFile";

export type DownloadState = "loading" | "ready" | "unavailable";

export default function useResultSetDownload(): {
  state: DownloadState;
  download: () => void;
} {
  const { t } = useTranslation();
  const { resultSetId } = useParams();
  const customerData = useRouteLoaderData("customer") as FieldsData | undefined;
  const resultSets = useRouteLoaderData("field") as ResultSet[] | undefined;
  const resultSetData = useRouteLoaderData("resultSet") as
    | { data: Promise<ResultSetGeoJSON | null> }
    | undefined;
  const pending = resultSetData?.data;

  const [geojson, setGeojson] = useState<ResultSetGeoJSON | null>(null);
  const [state, setState] = useState<DownloadState>("loading");

  useEffect(() => {
    setState("loading");
    setGeojson(null);
    if (!pending) {
      return;
    }
    let current = true;
    pending.then(
      (data) => {
        if (!current) return;
        setGeojson(data);
        setState(data ? "ready" : "unavailable");
      },
      () => {
        if (current) setState("unavailable");
      },
    );
    return () => {
      current = false;
    };
  }, [pending]);

  const download = () => {
    if (!geojson) return;

    const resultSet = resultSets?.find(({ id }) => id === resultSetId);
    const fieldName = customerData?.fields.features.find(
      (feature) => `${feature.properties.id}` === `${resultSet?.field_id}`,
    )?.properties.name;
    const filename = [
      translateProductName(t, resultSet?.product_sku || ""),
      fieldName,
      resultSet?.detection_dates[0],
    ]
      .filter((component) => component)
      .join("_");

    downloadFile(
      new File([JSON.stringify(geojson)], `${filename}.geojson`, {
        type: "application/json",
      }),
    );
  };

  return { state, download };
}
